import { useEffect, useState, useRef, useCallback } from "react";
import { MapContainer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Button } from "@/components/ui/button";
import { X, Loader2, Navigation, Check, Plus, Minus, MapPin, Search } from "lucide-react";
import MapTiles from "@/components/map/MapTiles";
import MapStyleSwitcher from "@/components/map/MapStyleSwitcher";
import { DEFAULT_MAP_STYLE } from "@/components/map/mapStyles";
import { api } from "@/lib/api";

// Hood center coordinates (default)
const DEFAULT_CENTER = [34.3419, 45.3866];
const DEFAULT_ZOOM = 15;

// Reads map movement and reports back the visible center coordinates
function CenterReader({ onChange }) {
  const map = useMap();
  useEffect(() => {
    const handler = () => {
      const c = map.getCenter();
      onChange({ lat: c.lat, lng: c.lng });
    };
    map.on("moveend", handler);
    map.on("zoomend", handler);
    // Fire once for initial center
    handler();
    return () => {
      map.off("moveend", handler);
      map.off("zoomend", handler);
    };
  }, [map, onChange]);
  return null;
}

// Exposes the map instance so we can pan programmatically (from "locate me" button)
function MapController({ onReady }) {
  const map = useMap();
  useEffect(() => {
    // Ensure Leaflet knows the current container size and enables touch gestures.
    // Without this, some devices (esp. iOS in portals) render the map but touch
    // drag does nothing because Leaflet was initialized before the container
    // was visible.
    try {
      map.invalidateSize();
      map.dragging.enable();
      map.touchZoom.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
      // Fire again after transitions/paint settle
      setTimeout(() => map.invalidateSize(), 200);
      setTimeout(() => map.invalidateSize(), 600);
    } catch { /* ignore */ }
    onReady(map);
  }, [map, onReady]);
  return null;
}

// Reverse-geocode. Tries our backend proxy first (server-side cache),
// then falls back to a direct call from the user's browser (avoids the pod
// shared-IP rate limit that Nominatim enforces).
async function reverseGeocode(lat, lng, signal) {
  // 1) Try backend proxy (fast if cached)
  try {
    const r = await api.get(`/geo/reverse?lat=${lat}&lng=${lng}&lang=ar`, { signal });
    if (r.data?.area) return r.data.area;
  } catch {
    /* ignore — try direct next */
  }
  // 2) Direct call from the user's IP (Nominatim usage policy: 1 req/sec/IP)
  try {
    const resp = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=16&accept-language=ar`,
      { signal, headers: { "Accept": "application/json" } }
    );
    if (!resp.ok) return null;
    const j = await resp.json();
    const a = j.address || {};
    return (
      a.suburb || a.neighbourhood || a.village || a.town || a.hamlet ||
      a.city_district || a.district || a.city || a.county || a.state || j.display_name || null
    );
  } catch {
    return null;
  }
}

/**
 * Fullscreen interactive location picker.
 * User drags the map underneath a fixed center pin (Google Maps / Airbnb style).
 * @param {object} props
 * @param {{lat: number, lng: number}=} props.initial — starting position
 * @param {(coords: {lat:number, lng:number, address?:string}) => void} props.onConfirm
 * @param {() => void} props.onCancel — called when user cancels
 */
export default function LocationPickerMap({ initial, onConfirm, onCancel }) {
  const start = initial && initial.lat && initial.lng ? [initial.lat, initial.lng] : DEFAULT_CENTER;
  const [coords, setCoords] = useState({ lat: start[0], lng: start[1] });
  const [locating, setLocating] = useState(false);
  const [mapStyle, setMapStyle] = useState(DEFAULT_MAP_STYLE);
  const [address, setAddress] = useState("");
  const [addressLoading, setAddressLoading] = useState(false);
  const mapRef = useRef(null);
  const geocodeTimer = useRef(null);
  const geocodeAbort = useRef(null);
  // Area search state — user can type a Khanaqin neighborhood name and
  // the map re-centers on the pick.
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchFocused, setSearchFocused] = useState(false);
  const searchTimer = useRef(null);

  const runSearch = useCallback((q) => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!q || q.trim().length < 2) {
      setSearchResults([]); setSearchBusy(false);
      return;
    }
    setSearchBusy(true);
    searchTimer.current = setTimeout(async () => {
      let results = [];
      try {
        const r = await api.get(`/geo/search?q=${encodeURIComponent(q.trim())}&limit=6`);
        if (Array.isArray(r.data) && r.data.length > 0) results = r.data;
      } catch { /* ignore */ }
      if (results.length === 0) {
        try {
          const resp = await fetch(
            `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(q.trim())}&format=json&addressdetails=1&limit=6&countrycodes=iq&viewbox=45.15,34.55,45.60,34.15&bounded=1&accept-language=ar`,
            { headers: { "Accept": "application/json" } }
          );
          if (resp.ok) {
            const j = await resp.json();
            results = (j || []).map(item => {
              const a = item.address || {};
              const area = a.suburb || a.neighbourhood || a.village || a.town || a.hamlet ||
                           a.city_district || a.district || a.city || a.county || a.state || item.display_name || "";
              let name = item.display_name || area || q;
              if (typeof name === "string" && name.includes(",")) {
                name = name.split(",").slice(0, 3).map(s => s.trim()).join(", ");
              }
              return { name, area, lat: parseFloat(item.lat), lng: parseFloat(item.lon) };
            }).filter(r => Number.isFinite(r.lat) && Number.isFinite(r.lng));
          }
        } catch { /* ignore */ }
      }
      setSearchResults(results);
      setSearchBusy(false);
    }, 350);
  }, []);

  // Lock body scroll while modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Debounced reverse-geocoding on coordinate change
  useEffect(() => {
    if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    if (geocodeAbort.current) geocodeAbort.current.abort();
    setAddressLoading(true);
    geocodeTimer.current = setTimeout(async () => {
      const ctrl = new AbortController();
      geocodeAbort.current = ctrl;
      const label = await reverseGeocode(coords.lat, coords.lng, ctrl.signal);
      if (!ctrl.signal.aborted) {
        setAddress(label || "");
        setAddressLoading(false);
      }
    }, 500);
    return () => {
      if (geocodeTimer.current) clearTimeout(geocodeTimer.current);
    };
  }, [coords.lat, coords.lng]);

  const handleLocateMe = () => {
    if (!("geolocation" in navigator)) return;
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (mapRef.current) {
          mapRef.current.setView([latitude, longitude], 17, { animate: true });
        }
        setCoords({ lat: latitude, lng: longitude });
        setLocating(false);
      },
      () => setLocating(false),
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleZoom = useCallback((delta) => {
    if (!mapRef.current) return;
    const z = mapRef.current.getZoom();
    mapRef.current.setZoom(z + delta);
  }, []);

  const handleConfirm = () => {
    onConfirm && onConfirm({ ...coords, address });
  };

  // Pin color adapts to map style (dark pin on satellite for contrast)
  const isDarkTile = mapStyle === "satellite" || mapStyle === "hybrid";
  const pinPrimary = "#22c55e";
  const pinSecondary = "#15803d";

  return (
    <div
      className="fixed inset-0 bg-slate-950"
      style={{ zIndex: 2147483000 }}
      data-testid="location-picker-map"
      role="dialog"
      aria-modal="true"
    >
      {/* Map */}
      <MapContainer
        center={start}
        zoom={DEFAULT_ZOOM}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
          touchAction: "none",  // let Leaflet own the gestures
        }}
        zoomControl={false}
        attributionControl={false}
        // Explicit gesture flags — belt-and-braces, some devices need them on init
        dragging={true}
        touchZoom={true}
        doubleClickZoom={true}
        scrollWheelZoom={true}
        tap={true}
      >
        <MapTiles styleId={mapStyle} />
        <CenterReader onChange={setCoords} />
        <MapController onReady={(m) => { mapRef.current = m; }} />
      </MapContainer>

      {/* Fixed center pin overlay (pointer-events none so map is draggable underneath) */}
      <div className="pointer-events-none absolute inset-0 z-[10] flex items-center justify-center">
        <div className="relative flex flex-col items-center -translate-y-6">
          {/* Teardrop pin (SVG) with pulse ring */}
          <div className="relative">
            {/* Pulse ring — centered UNDER the pin (bottom of teardrop) */}
            <span
              className={`absolute pointer-events-none rounded-full animate-ping ${isDarkTile ? "bg-emerald-400/35" : "bg-primary/25"}`}
              style={{
                width: "56px",
                height: "56px",
                left: "50%",
                bottom: "-8px",
                transform: "translateX(-50%)",
              }}
            />
            {/* Marker */}
            <svg
              width="56"
              height="70"
              viewBox="0 0 52 64"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="relative drop-shadow-[0_10px_20px_rgba(0,0,0,0.6)]"
            >
              <defs>
                <linearGradient id="pinGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={pinPrimary} />
                  <stop offset="100%" stopColor={pinSecondary} />
                </linearGradient>
              </defs>
              {/* Teardrop body */}
              <path
                d="M26 2C13.85 2 4 11.85 4 24c0 15.5 18.5 34 21 36.8 0.55 0.6 1.45 0.6 2 0C29.5 58 48 39.5 48 24 48 11.85 38.15 2 26 2Z"
                fill="url(#pinGrad)"
                stroke="#ffffff"
                strokeWidth="3.5"
              />
              {/* Inner circle */}
              <circle cx="26" cy="24" r="8" fill="#ffffff" />
              {/* Center dot */}
              <circle cx="26" cy="24" r="3.5" fill={pinSecondary} />
            </svg>
          </div>
          {/* Ground shadow */}
          <div className="mt-1 w-5 h-2 rounded-full bg-black/60 blur-[3px]" />
        </div>
      </div>

      {/* Top bar: cancel + search + locate-me + style-switcher */}
      <div className="absolute top-0 inset-x-0 z-[20] p-3 pt-safe flex items-center gap-2">
        <button
          type="button"
          onClick={onCancel}
          data-testid="map-cancel-btn"
          className="size-11 shrink-0 rounded-full bg-slate-900/90 backdrop-blur border border-white/15 text-white flex items-center justify-center shadow-xl hover:bg-slate-800 transition"
          aria-label="إلغاء"
        >
          <X className="size-5" />
        </button>
        {/* Search input + suggestions */}
        <div className="flex-1 relative min-w-0">
          <div className="relative">
            <Search className="absolute end-3 top-1/2 -translate-y-1/2 size-4 text-white/60 pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); runSearch(e.target.value); }}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
              placeholder="ابحث عن منطقة في خانقين..."
              className="w-full h-11 rounded-full bg-slate-900/90 backdrop-blur border border-white/15 pl-4 pr-10 text-white text-sm placeholder-white/50 shadow-xl focus:outline-none focus:border-primary transition"
              data-testid="map-search-input"
            />
          </div>
          {/* Suggestions dropdown */}
          {searchFocused && (searchBusy || searchResults.length > 0) && (
            <div
              className="absolute top-full inset-x-0 mt-1.5 rounded-2xl bg-slate-900/95 backdrop-blur-xl border border-white/15 shadow-2xl overflow-hidden max-h-72 overflow-y-auto"
              data-testid="map-search-suggestions"
            >
              {searchBusy && (
                <div className="px-3 py-2.5 text-xs text-white/60 flex items-center gap-1.5">
                  <Loader2 className="size-3 animate-spin" /> جاري البحث…
                </div>
              )}
              {!searchBusy && searchResults.length === 0 && (
                <div className="px-3 py-2.5 text-xs text-white/60">لا توجد نتائج في خانقين</div>
              )}
              {searchResults.map((s, i) => (
                <button
                  key={`${s.lat}-${s.lng}-${i}`}
                  type="button"
                  onClick={() => {
                    if (mapRef.current) {
                      try { mapRef.current.setView([s.lat, s.lng], 16); } catch { /* ignore */ }
                    }
                    setSearchResults([]);
                    setSearchFocused(false);
                    setSearchQuery(s.area || s.name);
                  }}
                  className="w-full text-start px-3 py-2.5 hover:bg-white/10 transition flex items-start gap-2 border-t border-white/5 first:border-t-0"
                  data-testid={`map-search-result-${i}`}
                >
                  <MapPin className="size-4 text-primary shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1 text-white">
                    <div className="text-sm font-semibold line-clamp-1">{s.area || s.name}</div>
                    {s.name && s.name !== s.area && (
                      <div className="text-[10px] text-white/50 line-clamp-1">{s.name}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={handleLocateMe}
          disabled={locating}
          data-testid="map-locate-me-btn"
          className="size-11 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-xl hover:bg-primary/90 transition disabled:opacity-60"
          aria-label="موقعي الحالي"
        >
          {locating ? <Loader2 className="size-5 animate-spin" /> : <Navigation className="size-5" />}
        </button>
      </div>

      {/* Style switcher — top-right, compact dropdown */}
      <div className="absolute top-[70px] end-3 z-[20]">
        <MapStyleSwitcher value={mapStyle} onChange={setMapStyle} />
      </div>

      {/* Zoom controls (right side, vertical) */}
      <div className="absolute end-4 top-1/2 -translate-y-1/2 z-[20] flex flex-col gap-2">
        <button
          type="button"
          onClick={() => handleZoom(1)}
          data-testid="map-zoom-in"
          aria-label="تكبير"
          className="size-10 rounded-full bg-slate-900/90 backdrop-blur border border-white/15 text-white flex items-center justify-center shadow-xl hover:bg-slate-800 transition"
        >
          <Plus className="size-4" />
        </button>
        <button
          type="button"
          onClick={() => handleZoom(-1)}
          data-testid="map-zoom-out"
          aria-label="تصغير"
          className="size-10 rounded-full bg-slate-900/90 backdrop-blur border border-white/15 text-white flex items-center justify-center shadow-xl hover:bg-slate-800 transition"
        >
          <Minus className="size-4" />
        </button>
      </div>

      {/* Bottom sheet */}
      <div className="absolute bottom-0 inset-x-0 z-[20] bg-slate-900/95 backdrop-blur-xl border-t border-white/10 rounded-t-[28px] p-5 pb-safe shadow-[0_-8px_32px_rgba(0,0,0,0.5)]">
        <div className="mx-auto w-12 h-1 rounded-full bg-white/20 mb-4" />

        {/* Address / Area name */}
        <div className="text-center mb-3">
          <div className="text-[11px] font-bold text-primary uppercase tracking-wider mb-1 flex items-center justify-center gap-1">
            <MapPin className="size-3" /> المنطقة الحالية
          </div>
          <div className="text-white text-base font-bold min-h-[24px]" data-testid="picked-address">
            {addressLoading ? (
              <span className="text-white/50 text-sm inline-flex items-center gap-1.5">
                <Loader2 className="size-3.5 animate-spin" /> جاري تحديد المنطقة...
              </span>
            ) : (
              address || <span className="text-white/50 text-sm">لم يتم التعرف على المنطقة</span>
            )}
          </div>
          <div className="text-[11px] text-white/40 font-mono mt-1 dir-ltr">
            {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={onCancel}
            className="flex-1 h-12 rounded-full bg-transparent border-white/20 text-white hover:bg-white/10"
            data-testid="map-cancel-sheet-btn"
          >
            إلغاء
          </Button>
          <Button
            onClick={handleConfirm}
            className="flex-[2] h-12 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground gap-2 font-bold shadow-lg glow-primary"
            data-testid="map-confirm-btn"
          >
            <Check className="size-5" />
            تأكيد الموقع
          </Button>
        </div>
      </div>
    </div>
  );
}
